import React, { useEffect, useState, useRef } from "react";
import { FaArrowRightLong } from "react-icons/fa6";
import { MdDeleteOutline } from "react-icons/md";
import Select from 'react-select';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getWithTokenAPI, postWithTokenAPI, postWithoutTokenAPI, putAPI, deleteAPI, UploadDocument } from "../../utils/Api";
import { modules, formats } from "../../utils/QuillConfig";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../css/blogmanagement.css"
import { useNavigate } from "react-router-dom";
import {
  CircularProgress,
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Switch
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarTodayIcon,
  Receipt as ReceiptIcon,
  LocalOffer as LocalOfferIcon
} from "@mui/icons-material";
import {
  Edit as EditIcon
} from "@mui/icons-material";
import FeedIcon from '@mui/icons-material/Feed';
const primaryGreen = "#DC3545";
const lightGreen = "#ffe5e5";
const darkGreen = "#b02a37";

const BlogService = {
  fetchAll: async (token, filter = "All") => {
    return await getWithTokenAPI(token, `/blog/find-all?filterBy=${filter}`);
  },

  update: async (token, blogData) => {
    return await putAPI(token, "/blog/update", blogData, true);
  },

  delete: async (token, id) => {
    return await deleteAPI(token, '/blog/delete', { _id: id });
  },

  updateStatus: async (token, id, newStatus) => {
    return await putAPI(token, "/blog/update", {
      _id: id,
      blog_status: newStatus
    });
  }
};

function BlogManagement() {
  let navigate = useNavigate()
  const [state, setState] = useState({
    blogs: [],
    loading: false,
    error: null,
    isLoading: false,
    statusFilter: "All"
  });

  const token = localStorage.getItem("token");
  const permissions = JSON.stringify(localStorage.getItem("permissions"))

  const updateState = (newState) => {
    setState(prev => ({ ...prev, ...newState }));
  };

  const fetchBlogs = async () => {
    updateState({ loading: true, error: null });
    try {
      const response = await BlogService.fetchAll(token, state.statusFilter);
      if (response?.statusCode === 200) {
        const data = Array.isArray(response.data) ? response.data : response.data.blogs;
        updateState({ blogs: data });
      } else {
        updateState({ error: "Failed to load blogs" });
      }
    } catch (err) {
      updateState({ error: "Error fetching blogs" });
    } finally {
      updateState({ loading: false }); 
    }
  };

  const handleDeleteBlog = async (id) => {
    if (!window.confirm("Are you sure you want to delete this blog?")) {
      return;
    }
    try {
      const response = await BlogService.delete(token, id);
      if (response?.statusCode === 200 || response?.success) {
        toast.success("Blog deleted successfully!", { autoClose: 1000 });
        fetchBlogs();
      } else {
        toast.error("Failed to delete blog: " + (response?.message || "Unknown error"));
      }
    } catch (err) {
      toast.error('Delete failed: ' + err.message);
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      const response = await BlogService.updateStatus(token, id, newStatus);
      if (response?.statusCode === 200) {
        toast.success("Blog status updated successfully!")
        fetchBlogs();
      } else {
        toast.error("Failed to update blog status");
      }
    } catch (err) {
      toast.error("Error creating blog: " + err.message);
    }
  };

  const prepareEditForm = (blog) => {
    navigate(`/backoffice/blogmanagementupdate/${blog._id}`)
  };

  const filteredBlogs = state.blogs.filter(blog => {
    switch (state.statusFilter) { 
      case "All":
        return blog.blog_status === "published" || blog.blog_status === "draft";
      case "draft":
        return (blog.blog_status !== "published")
      case "Active":
        return blog.blog_status === "published";
      default:
        return false; 
    }
  });

  useEffect(() => {
    fetchBlogs();
  }, [state.statusFilter]); 

  return (
    <div className="container-fluid py-3 m-1 blogmanagement">
      <ToastContainer position="top-center" autoClose={1500} />
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 3,
        p: 2,
        backgroundColor: lightGreen,
        borderRadius: 2,
        borderLeft: `5px solid ${primaryGreen}`
      }}>
        <FeedIcon sx={{ color: primaryGreen, fontSize: 50, mr: 2 }} />
        <Box>
          <Typography variant="h4" component="h1" sx={{ color: primaryGreen, fontWeight: 'bold' }}>
            Blog
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Manage blogs
          </Typography>
        </Box>
      </Box>

      <div className="row justify-content-between align-items-center" style={{ margin: "0px 10px 0px 0px" }}>
        <div className="col-5 m-0">
          <div className="btn-group" style={{ backgroundColor: lightGreen }} role="group">
            <button onClick={() => updateState({ statusFilter: "All" })} className="btn rounded"
              style={state.statusFilter == "All" ? { backgroundColor: "#DC3545", color: "white" } : { backgroundColor: "white", color: "#DC3545" }}
            >All</button>
            <button onClick={() => updateState({ statusFilter: "Active" })} className="btn rounded"
              style={state.statusFilter == "Active" ? { backgroundColor: "#DC3545", color: "white" } : { backgroundColor: "white", color: "#DC3545" }}
            >Published</button>
            <button onClick={() => updateState({ statusFilter: "draft" })} className="btn rounded"
              style={state.statusFilter == "draft" ? { backgroundColor: "#DC3545", color: "white" } : { backgroundColor: "white", color: "#DC3545" }}
            >Unpublished</button>
          </div>
        </div>

        {
          permissions.includes("create_blog") && (
            <div className="col-2 row justify-content-end">
              <Button
                variant="contained"
                sx={{
                  backgroundColor: primaryGreen,
                  "&:hover": { backgroundColor: "white", border: "2px solid #DC3545", color: "#DC3545", },
                  color: "white",
                  fontWeight: "bold",
                  textTransform: "none",
                  width: { xs: "100%", md: "auto" }, 
                  mt: { xs: 2, md: 0 },
                }}
                onClick={() => navigate("/backoffice/blogcreate")}
              >
                <i className="bi bi-plus-circle me-2"></i>Add
              </Button>
            </div>

          )
        }
      </div>

      {/* Blog Table */}
      {state.loading ? ( 
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 200,
          mt: 2,
        }}>
          <CircularProgress sx={{ color: primaryGreen }} />
          <Typography sx={{ ml: 2, color: primaryGreen }}>Loading blogs...</Typography>
        </Box>
      ) : state.error ? (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 200,
          color: 'error.main'
        }}>
          <Typography>{state.error}</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2, mt: 3 }}>
          <Table sx={{ minWidth: 650 }} aria-label="blog table">
            <TableHead>
              <TableRow sx={{ backgroundColor: lightGreen }}>
                <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>S.No</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Author</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }} align="center">Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBlogs.length > 0 ? (
                filteredBlogs.map((blog, index) => (
                  <TableRow
                    key={blog._id}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Typography >
                        {blog.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {blog.author?.name || 'N/A'}
                          </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Switch
                          checked={blog.blog_status === "published"}
                          onChange={() => handleStatusUpdate(
                            blog._id,
                            blog.blog_status === "published" ? "draft" : "published"
                          )}
                          color="success"
                        />
                        <Chip
                          label={blog.blog_status === "published" ? "published" : "unpublished"}
                          size="small"
                          sx={{
                            ml: 1,
                            backgroundColor: blog.blog_status === "published" ? '#ffe5e5' : '#ffebee',
                            color: blog.blog_status === "published" ? primaryGreen : '#c62828',
                            textTransform: 'capitalize'
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      {new Date(blog.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        {
                          permissions.includes("update_blog") && (
                            <IconButton
                              onClick={() => prepareEditForm(blog)}
                              sx={{ color: primaryGreen }}
                            >
                              <EditIcon />
                            </IconButton>
                          )
                        }
                        {
                          permissions.includes("delete_blog") && (
                            <IconButton
                              onClick={() => handleDeleteBlog(blog._id)}
                              sx={{ color: '#d32f2f' }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )
                        }
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <FeedIcon sx={{ fontSize: 60, mr: 2 }} />
                    <Typography variant="h6">No blogs found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

    </div>
  );
}

export default BlogManagement;